import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { FaCopy, FaShareAlt, FaLocationArrow, FaPen } from "react-icons/fa";
import { Share } from "@capacitor/share";
import {
  subscribeEvent,
  subscribeParticipants,
  endEvent,
  extendEvent,
  leaveEvent,
  updateEventName,
  updateEventAddress,
  recordJoinedEvent,
  eventShareUrl,
  VISIBILITY_OPTIONS,
} from "../utils/events";
import AddressPicker from "./AddressPicker";
import { useNotifications } from "./NotificationsContext";
import { registerPush } from "../utils/push";
import "../CSS/App.css";

function formatCountdown(ms) {
  if (ms <= 0) return "Expired";
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
}

// Quick "keep visible for" choices for the host's Extend control (the timed
// windows; "Until I end it" isn't an extend action).
const EXTEND_OPTIONS = VISIBILITY_OPTIONS.filter((o) => o.id !== "until");

// Shared event screen. The host sees the code, participants, and an "End
// event" button; a guest who reached here by joining sees the same code +
// participants plus the revealed address.
const EventDetails = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const { code } = useParams();
  const [uid, setUid] = useState(null);
  const [event, setEvent] = useState(undefined); // undefined = loading
  const [participants, setParticipants] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressDraft, setAddressDraft] = useState(null); // { addressTitle, address }
  const [savingAddress, setSavingAddress] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [extending, setExtending] = useState(false);
  const { unreadByCode, markEventRead } = useNotifications();
  const [addressUpdated, setAddressUpdated] = useState(false);
  const bannerHandledRef = useRef(false);

  // If the user arrived here with unread address-change notifications for this
  // event (e.g. they missed the push, or never granted it), surface a banner and
  // clear those notifications - seeing the new address is the acknowledgement.
  useEffect(() => {
    if (bannerHandledRef.current) return;
    if (code && unreadByCode[code] > 0) {
      bannerHandledRef.current = true;
      setAddressUpdated(true);
      markEventRead(code);
    }
  }, [code, unreadByCode, markEventRead]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) navigate("/login");
      else setUid(currentUser.uid);
    });
    return () => unsub();
  }, [auth, navigate]);

  useEffect(() => {
    if (!code) return;
    const unsubEvent = subscribeEvent(code, setEvent);
    return () => unsubEvent();
  }, [code]);

  // Tick the countdown once a minute.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const isHost = event && uid && event.hostUid === uid;
  const isGuest = event && uid && !isHost;

  // Only the host reads the participant list (guests can't see who joined -
  // enforced in firestore.rules too), so only the host subscribes.
  useEffect(() => {
    if (!code || !isHost) return;
    const unsub = subscribeParticipants(code, setParticipants);
    return () => unsub();
  }, [code, isHost]);

  // For a guest viewing the event: make sure they have a push token on file
  // (so an address change notifies them), and make sure this event is recorded
  // on their user doc so it lists in the Events tab on every device - this also
  // backfills events joined before joinedEventCodes existed.
  useEffect(() => {
    if (isGuest && uid) {
      registerPush(uid);
      recordJoinedEvent(uid, code);
    }
  }, [isGuest, uid, code]);

  const openDirections = () => {
    if (!event?.address) return;
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        event.address,
      )}`,
      "_blank",
    );
  };

  const handleLeave = async () => {
    try {
      await leaveEvent({ code, uid });
    } catch (err) {
      console.error("Error leaving event:", err);
    } finally {
      // leaveEvent deletes the participant doc, which is what getMyJoinedEvents
      // reads - so once it's gone the event drops off every device on its own.
      navigate("/");
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  const shareCode = async () => {
    try {
      // Share the public link: a guest can tap it to see the address in a
      // browser (no install/sign-in) and is prompted to get the app. The link
      // lives in the message text only - passing it as `url` too made some
      // share targets (e.g. email) append it a second time.
      const link = eventShareUrl(code);
      await Share.share({
        title: "You're invited on iBlink",
        text: `You're invited! Tap to see the address: ${link}\nOr open the iBlink app and join with code ${code}.`,
        dialogTitle: "Share event",
      });
    } catch (err) {
      console.error("Error sharing code:", err);
    }
  };

  const startEditName = () => {
    setNameDraft(event?.name || "");
    setEditingName(true);
  };

  const saveName = async () => {
    setSavingName(true);
    try {
      await updateEventName(code, nameDraft);
      setEditingName(false);
    } catch (err) {
      console.error("Error updating event name:", err);
    } finally {
      setSavingName(false);
    }
  };

  const saveAddress = async () => {
    if (!addressDraft) return;
    setSavingAddress(true);
    try {
      await updateEventAddress(code, addressDraft);
      setEditingAddress(false);
      setAddressDraft(null);
    } catch (err) {
      console.error("Error updating event address:", err);
    } finally {
      setSavingAddress(false);
    }
  };

  const handleExtend = async (ms) => {
    setExtending(true);
    try {
      await extendEvent(code, ms);
      setExtendOpen(false);
    } catch (err) {
      console.error("Error extending event:", err);
    } finally {
      setExtending(false);
    }
  };

  const handleEnd = async () => {
    try {
      await endEvent(code);
    } catch (err) {
      console.error("Error ending event:", err);
    } finally {
      // The host list is queried live from Firestore; ending sets active:false
      // there, so it drops off on its own - just leave the screen.
      navigate("/");
    }
  };

  if (event === undefined) {
    return (
      <div className="container" style={{ padding: "20px" }}>
        <p>Loading event...</p>
      </div>
    );
  }

  if (event === null) {
    return (
      <div
        className="container"
        style={{ width: "80%", maxWidth: "400px", margin: "auto" }}
      >
        <h2>Event not found</h2>
        <button
          type="button"
          onClick={() => navigate("/")}
          style={{
            backgroundColor: "gray",
            color: "white",
            padding: "10px 15px",
            border: "none",
            cursor: "pointer",
            borderRadius: "5px",
          }}
        >
          Back
        </button>
      </div>
    );
  }

  const expiresMs = event.expiresAt ? event.expiresAt.toMillis() - now : 0;
  const ended = event.active === false || expiresMs <= 0;

  return (
    <div
      className="container"
      style={{
        width: "85%",
        maxWidth: "420px",
        margin: "auto",
        paddingTop: "calc(20px + env(safe-area-inset-top, 0px))",
      }}
    >
      {/* Title. The host can rename the event inline (pencil); guests just see
          it. A custom name wins, else "Your Event" (host) / the host's name. */}
      {isHost && editingName ? (
        <div style={{ marginBottom: "8px" }}>
          <input
            type="text"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            maxLength={60}
            placeholder="e.g. Sarah's Birthday"
            autoFocus
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 12px",
              borderRadius: "10px",
              border: "1px solid var(--border-color, #ccc)",
              backgroundColor: "var(--item-bg, #fff)",
              color: "var(--text-primary, #1a1a1a)",
              fontSize: "18px",
            }}
          />
          <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
            <button
              type="button"
              onClick={saveName}
              disabled={savingName}
              style={{
                backgroundColor: "#4CAF50",
                color: "white",
                padding: "8px 16px",
                border: "none",
                borderRadius: "6px",
                cursor: savingName ? "default" : "pointer",
              }}
            >
              {savingName ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setEditingName(false)}
              disabled={savingName}
              style={{
                backgroundColor: "transparent",
                color: "var(--text-secondary, #777)",
                padding: "8px 16px",
                border: "1px solid var(--border-color, #ccc)",
                borderRadius: "6px",
                cursor: savingName ? "default" : "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <h2
          style={{
            marginBottom: "4px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <span>
            {event.name || (isHost ? "Your Event" : `${event.hostName}'s Event`)}
          </span>
          {isHost && !ended && (
            <button
              type="button"
              onClick={startEditName}
              aria-label="Edit event name"
              title="Edit event name"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--text-secondary, #777)",
                padding: 0,
                display: "inline-flex",
              }}
            >
              <FaPen size={14} />
            </button>
          )}
        </h2>
      )}
      <p
        style={{
          color: ended ? "#dc3545" : "var(--text-secondary, #777)",
          fontSize: "13px",
          marginTop: 0,
        }}
      >
        {ended ? "This event has ended." : formatCountdown(expiresMs)}
      </p>

      {/* Host can keep a running event visible longer (re-arms the countdown). */}
      {isHost && !ended && !editingAddress && (
        <div style={{ marginTop: "-2px", marginBottom: "10px" }}>
          {!extendOpen ? (
            <button
              type="button"
              onClick={() => setExtendOpen(true)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "#007BFF",
                fontSize: "13px",
              }}
            >
              Extend
            </button>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  color: "var(--text-secondary, #777)",
                }}
              >
                Keep visible for:
              </span>
              {EXTEND_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => handleExtend(o.ms)}
                  disabled={extending}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "999px",
                    border: "1px solid var(--border-color, #ccc)",
                    backgroundColor: "var(--chip-bg, #f6f6f6)",
                    color: "var(--text-primary, #1a1a1a)",
                    cursor: extending ? "default" : "pointer",
                    fontSize: "13px",
                  }}
                >
                  {o.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setExtendOpen(false)}
                disabled={extending}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "var(--text-secondary, #777)",
                  fontSize: "13px",
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* The address changed and the user hadn't seen it yet - make it obvious. */}
      {addressUpdated && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            padding: "12px 14px",
            borderRadius: "12px",
            backgroundColor: "rgba(0,123,255,0.10)",
            border: "1px solid rgba(0,123,255,0.35)",
            margin: "0 0 12px",
          }}
        >
          <FaLocationArrow
            size={15}
            color="#007bff"
            style={{ marginTop: "2px", flexShrink: 0 }}
          />
          <div
            style={{
              flex: 1,
              fontSize: "14px",
              color: "var(--text-primary, #1a1a1a)",
            }}
          >
            <b>The address was updated.</b> Here's the latest location and
            directions.
          </div>
          <button
            type="button"
            onClick={() => setAddressUpdated(false)}
            aria-label="Dismiss"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--text-secondary, #777)",
              fontSize: "18px",
              lineHeight: 1,
              flexShrink: 0,
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Code display */}
      <div
        style={{
          textAlign: "center",
          padding: "18px",
          borderRadius: "14px",
          border: "1px solid var(--border-color, #ddd)",
          backgroundColor: "var(--chip-bg, #f0f0f0)",
          margin: "12px 0",
        }}
      >
        <div
          style={{
            fontSize: "12px",
            color: "var(--text-secondary, #777)",
            marginBottom: "4px",
          }}
        >
          Event code
        </div>
        <div
          style={{
            fontSize: "34px",
            fontWeight: "bold",
            letterSpacing: "6px",
            color: "var(--text-primary, #1a1a1a)",
          }}
        >
          {code}
        </div>
        {!ended && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              marginTop: "16px",
            }}
          >
            {/* Share is the primary action - it's how guests are invited and how
                the app spreads, so it's the loud, filled button. */}
            <button
              type="button"
              onClick={shareCode}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                width: "100%",
                padding: "14px 20px",
                border: "none",
                borderRadius: "12px",
                backgroundColor: "#007BFF",
                color: "white",
                fontSize: "16px",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 6px 16px -4px rgba(0, 123, 255, 0.5)",
              }}
            >
              <FaShareAlt size={18} /> Share invite
            </button>
            {/* Copy is the quiet secondary. */}
            <button
              type="button"
              onClick={copyCode}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: "100%",
                padding: "9px 16px",
                border: "1px solid var(--border-color, #ccc)",
                borderRadius: "10px",
                backgroundColor: "transparent",
                color: "var(--text-secondary, #555)",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <FaCopy size={15} /> {copied ? "Copied!" : "Copy code"}
            </button>
          </div>
        )}
      </div>

      {/* Address: hosts always see it; guests see it because they joined. The
          host can change it here; guests pick the new address up live via the
          subscribeEvent listener (no notification is sent). */}
      <div style={{ marginBottom: "12px" }}>
        <div style={{ fontSize: "12px", color: "var(--text-secondary, #777)" }}>
          Address
        </div>

        {isHost && editingAddress ? (
          <div style={{ marginTop: "8px" }}>
            <AddressPicker
              uid={uid}
              onSelect={setAddressDraft}
              autoSelectFirst={false}
              previewLabel="New address"
            >
              {/* Save / Cancel sit here so AddressPicker renders "use my
                  current location" beneath them. */}
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  type="button"
                  onClick={saveAddress}
                  disabled={savingAddress || !addressDraft}
                  style={{
                    backgroundColor: addressDraft ? "#4CAF50" : "#9ccc9e",
                    color: "white",
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: "6px",
                    cursor:
                      savingAddress || !addressDraft ? "default" : "pointer",
                  }}
                >
                  {savingAddress ? "Saving..." : "Save address"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingAddress(false);
                    setAddressDraft(null);
                  }}
                  disabled={savingAddress}
                  style={{
                    backgroundColor: "transparent",
                    color: "var(--text-secondary, #777)",
                    padding: "8px 16px",
                    border: "1px solid var(--border-color, #ccc)",
                    borderRadius: "6px",
                    cursor: savingAddress ? "default" : "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </AddressPicker>
          </div>
        ) : (
          <>
            {/* Only the host sees their private title for the place; guests
                just get the actual address. */}
            {isHost && (
              <div style={{ fontWeight: 600 }}>{event.addressTitle}</div>
            )}
            <div>{event.address}</div>
            {isHost && !ended && (
              <button
                type="button"
                onClick={() => setEditingAddress(true)}
                style={{
                  background: "none",
                  border: "none",
                  padding: "4px 0 0",
                  marginTop: "2px",
                  cursor: "pointer",
                  color: "#007BFF",
                  fontSize: "13px",
                }}
              >
                Update address
              </button>
            )}
            {!isHost && (
              <div
                style={{
                  fontSize: "13px",
                  color: "var(--text-secondary, #777)",
                  marginTop: "4px",
                }}
              >
                Hosted by {event.hostName}
              </div>
            )}
          </>
        )}
      </div>

      {/* Navigate straight to the address. Hidden while the host is editing the
          address (the edit panel shows Save/Cancel instead); it returns once
          they save or cancel, since both clear editingAddress. */}
      {!ended && !editingAddress && (
        <button
          type="button"
          onClick={openDirections}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            padding: "12px",
            border: "none",
            borderRadius: "10px",
            backgroundColor: "#4CAF50",
            color: "white",
            fontSize: "15px",
            cursor: "pointer",
            marginBottom: "16px",
          }}
        >
          <FaLocationArrow size={15} /> Navigate
        </button>
      )}

      {/* Participants - host only. Guests don't see who else joined. Hidden
          while editing the address, to keep that a focused screen. */}
      {isHost && !editingAddress && (
        <div style={{ marginBottom: "16px" }}>
          <div
            style={{
              fontSize: "12px",
              color: "var(--text-secondary, #777)",
              marginBottom: "4px",
            }}
          >
            Joined ({participants.length})
          </div>
          {participants.length === 0 ? (
            <div style={{ fontSize: "14px" }}>No one has joined yet.</div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {participants.map((p) => (
                <li
                  key={p.id}
                  style={{
                    padding: "6px 0",
                    borderBottom: "1px solid var(--border-color, #eee)",
                  }}
                >
                  {p.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* End / Leave / Back - hidden while editing the address; Save/Cancel in
          the edit panel handle that flow and return here on their own. */}
      {!editingAddress && (
      <div style={{ display: "flex", gap: "12px" }}>
        {isHost && !ended && (
          <button
            type="button"
            onClick={handleEnd}
            style={{
              backgroundColor: "#dc3545",
              color: "white",
              padding: "10px 20px",
              border: "none",
              cursor: "pointer",
              borderRadius: "5px",
            }}
          >
            End Event
          </button>
        )}
        {isGuest && !ended && (
          <button
            type="button"
            onClick={handleLeave}
            style={{
              backgroundColor: "transparent",
              color: "#dc3545",
              padding: "10px 20px",
              border: "1px solid #dc3545",
              cursor: "pointer",
              borderRadius: "5px",
            }}
          >
            Leave Event
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate("/")}
          style={{
            backgroundColor: "gray",
            color: "white",
            padding: "10px 15px",
            border: "none",
            cursor: "pointer",
            borderRadius: "5px",
          }}
        >
          Back
        </button>
      </div>
      )}
    </div>
  );
};

export default EventDetails;
